import { Component, OnInit, OnDestroy, Inject, inject } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatChipsModule } from '@angular/material/chips';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DialogData, DialogEntry } from '../../Interfaces/dialog.interface';
import { A11yModule } from '@angular/cdk/a11y';

@Component({
  selector: 'av-dialog',
  templateUrl: './av-dialog.component.html',
  styleUrls: ['./av-dialog.component.scss', '../../../../../assets/theme/cutom/_av-dialog.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatChipsModule,
    MatButtonModule,
    MatTooltipModule,
    TranslateModule,
    A11yModule,
  ],
})
export class AvDialogComponent implements OnInit, OnDestroy {
  private readonly translate = inject(TranslateService);
  button_save!: string;
  button_cancel!: string;
  valueControl: FormControl<string>;
  private _destroy$ = new Subject<void>();

  constructor(
    public dialogRef: MatDialogRef<AvDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
  ) {
    this.valueControl = new FormControl(this.data?.value ?? '', { nonNullable: true });
  }

  ngOnInit() {
    this.initTexts();
    this.initValueSubscription();
  }

  private initValueSubscription(): void {
    this.valueControl.valueChanges.pipe(takeUntil(this._destroy$)).subscribe((value) => {
      this.data.value = value;
    });
  }

  ngOnDestroy() {
    this._destroy$.next();
    this._destroy$.complete();
  }

  initTexts(): void {
    this.button_save = this.translate.instant('shared.general.buttons.confirm');
    this.button_cancel = this.translate.instant('shared.general.buttons.cancel');
  }

  onNoClick(): void {
    this.dialogRef.close(false);
  }

  onClick(): void {
    this.dialogRef.close(this.data.value);
  }

  hasIcon() {
    return this.data.icon?.length && this.data.icon?.length > 0;
  }

  hasEntries(): boolean {
    return Array.isArray(this.data.entries) && this.data.entries.length > 0;
  }

  entryLabel(entry: DialogEntry): string {
    if (entry.labelKey) return this.translate.instant(entry.labelKey);
    return entry.label ?? '';
  }

  isEntryArray(value: unknown): value is unknown[] {
    return Array.isArray(value);
  }

  isBoolean(value: unknown): value is boolean {
    return typeof value === 'boolean';
  }

  entryValue(value: unknown): string {
    const na = this.translate.instant('shared.general.labels.na');
    if (value === null || value === undefined || value === '') return na;
    if (typeof value === 'string' || typeof value === 'number') return String(value);
    return String(value);
  }
}
